import { NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RouteId } from '../../../RouteId';
import {
  CommodityGroupDataGridColumnGroup,
  CommodityGroupDataGridField,
  CommodityGroupDataGridRow,
} from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  GridAlignmentType,
  gridColDefFactory,
  gridColumnGroupFactory,
  GridSortDirectionType,
  headerGroupWithIcon,
  LinkCell,
  LinkCellValue,
  numberFormatterFactory,
  TooltipCell,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { IconId } from '~/components/Icon';

const DEFAULT_WIDTH = 200;
export const useCommodityGroupDataGridConfig: UseDataGridConfig<CommodityGroupDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'Base.commodityGroup.columns' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'Base.commodityGroup.columnBands' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      sortModel: [{ field: CommodityGroupDataGridField.NAME, sort: GridSortDirectionType.ASC }],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: CommodityGroupDataGridField.NAME,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.commodityGroupDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 300,
          hideable: false,
        }),
        createColumn({
          field: CommodityGroupDataGridField.DESCRIPTION,
          renderCell: cellFactory(TooltipCell),
          minWidth: 400,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_COUNT_CURRENT_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: DEFAULT_WIDTH,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_COUNT_LAST_12_MONTHS,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: DEFAULT_WIDTH,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_COUNT_LAST_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: DEFAULT_WIDTH,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_CURRENT_YEAR,
          renderCell: cellFactory(CurrencyCell),
          minWidth: DEFAULT_WIDTH,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_LAST_12_MONTHS,
          renderCell: cellFactory(CurrencyCell),
          minWidth: DEFAULT_WIDTH,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_LAST_YEAR,
          renderCell: cellFactory(CurrencyCell),
          minWidth: DEFAULT_WIDTH,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_SHARE_CURRENT_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_SHARE_LAST_12_MONTHS,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_SHARE_LAST_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_CUMULATIVE_CURRENT_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_CUMULATIVE_LAST_12_MONTHS,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
        createColumn({
          field: CommodityGroupDataGridField.ORDER_VOLUME_CUMULATIVE_LAST_YEAR,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 120,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: CommodityGroupDataGridColumnGroup.COMMODITY_GROUP,
          renderHeaderGroup: headerGroupWithIcon(IconId.commodity),
          children: [
            //
            { field: CommodityGroupDataGridField.NAME },
            { field: CommodityGroupDataGridField.DESCRIPTION },
          ],
        }),
        createGroup({
          groupId: CommodityGroupDataGridColumnGroup.ORDER,
          renderHeaderGroup: headerGroupWithIcon(IconId.order),
          children: [
            //
            { field: CommodityGroupDataGridField.ORDER_COUNT_CURRENT_YEAR },
            { field: CommodityGroupDataGridField.ORDER_COUNT_LAST_YEAR },
            { field: CommodityGroupDataGridField.ORDER_COUNT_LAST_12_MONTHS },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_CURRENT_YEAR },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_LAST_YEAR },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_LAST_12_MONTHS },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_SHARE_CURRENT_YEAR },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_SHARE_LAST_YEAR },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_SHARE_LAST_12_MONTHS },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_CUMULATIVE_CURRENT_YEAR },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_CUMULATIVE_LAST_YEAR },
            { field: CommodityGroupDataGridField.ORDER_VOLUME_CUMULATIVE_LAST_12_MONTHS },
          ],
        }),
      ],
      exportModel: {},
    } as DataGridConfig<CommodityGroupDataGridRow>;
  }, [t, tColumn, tGroup]);
};
